import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link as RouterLink} from 'react-router';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    IconButton,
    Link,
    Paper,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faAngleLeft, faAngleRight, faArrowsRotate, faCode} from '@fortawesome/pro-solid-svg-icons';
import dayjs, {Dayjs} from 'dayjs';

import {useApplicationContext} from 'ApplicationContext';
import {useDocumentTitle} from 'Hooks/useDocumentTitle';
import {CalendarAccessDto, CalendarViewDto, EUserGroup} from 'apiClient/Api';

import {CalendarItem, toItems, weekDays} from './calendarModel';
import WeekGrid from './WeekGrid';
import MonthGrid from './MonthGrid';
import AgendaList from './AgendaList';
import EntryDialog from './EntryDialog';

/** Umfang des angezeigten Zeitfensters. */
type RangeMode = 'week' | 'month';

/** Takt, in dem die Jetzt-Linie nachgeführt wird. */
const NOW_TICK_MS = 60_000;

/**
 * Der eigene Labelwin-Kalender.
 *
 * Die Person ist nie Teil der Anfrage — der Server leitet sie aus dem LabelBenutzer des
 * angemeldeten Kontos ab. Diese Seite kann deshalb keinen fremden Kalender anzeigen, und es gibt
 * hier bewusst keine Personenauswahl.
 *
 * Vor dem ersten Abruf wird `GET /Calendar/Me/Access` gefragt. Der antwortet immer mit 200 und
 * sagt in Klartext, ob und warum der Kalender nicht zur Verfügung steht ("kein LabelBenutzer
 * hinterlegt" ist der häufigste Fall und ein Zustand, kein Fehler) — so muss die Seite nicht erst
 * in ein 403 laufen, um das herauszufinden.
 */
export const MyCalendar: React.FC = () => {
    const {t, i18n} = useTranslation();
    useDocumentTitle('calendar.title');
    const context = useApplicationContext();
    const theme = useTheme();
    const compact = useMediaQuery(theme.breakpoints.down('md'));

    const [access, setAccess] = useState<CalendarAccessDto | undefined>(undefined);
    const [accessLoading, setAccessLoading] = useState(true);
    const [view, setView] = useState<CalendarViewDto | undefined>(undefined);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const [anchor, setAnchor] = useState<Dayjs>(() => dayjs().startOf('day'));
    const [mode, setMode] = useState<RangeMode>('week');
    const [selected, setSelected] = useState<CalendarItem | null>(null);
    const [showRaw, setShowRaw] = useState(false);

    // Eigener Uhrzeit-Zustand: die Jetzt-Linie muss sich bewegen, ohne dass jemand neu lädt.
    // Bewusst die Zeit des Browsers — der Kalender wird von Menschen in derselben Zeitzone benutzt,
    // in der die Plantafel geführt wird.
    const [now, setNow] = useState<Dayjs>(() => dayjs());
    useEffect(() => {
        const timer = setInterval(() => setNow(dayjs()), NOW_TICK_MS);
        return () => clearInterval(timer);
    }, []);

    const isAdmin = (context.userData?.userGroup ?? EUserGroup.CUSTOMER) <= EUserGroup.ADMIN;

    const range = useMemo(() => {
        if (mode === 'month') {
            return {from: anchor.startOf('month'), to: anchor.endOf('month').startOf('day')};
        }
        const days = weekDays(anchor);
        return {from: days[0], to: days[6]};
    }, [anchor, mode]);

    useEffect(() => {
        setAccessLoading(true);
        context.api.calendar
            .calendarGetMyAccess()
            .then((response) => setAccess(response.data))
            .catch(context.apiError)
            .finally(() => setAccessLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const load = useCallback(() => {
        if (!access?.available) return;

        setLoading(true);
        setError(undefined);
        context.api.calendar
            .calendarGetMyCalendar({
                von: range.from.format('YYYY-MM-DD'),
                bis: range.to.format('YYYY-MM-DD'),
            })
            .then((response) => setView(response.data))
            .catch((e: any) => {
                // Die Anbindung kann zwischen Access-Abfrage und Abruf wegbrechen (503) — dann
                // gehört die Begründung des Servers auf die Seite, nicht in eine Snackbar.
                const message = typeof e?.response?.data === 'string'
                    ? e.response.data
                    : e?.response?.data?.message ?? e?.message;
                setError(message ?? t('calendar.loadFailed'));
                setView(undefined);
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [access?.available, range.from, range.to]);

    useEffect(() => {
        load();
    }, [load]);

    const items = useMemo(() => toItems(view), [view]);
    const step = mode === 'week' ? 'week' : 'month';

    const rangeLabel = mode === 'week'
        ? `${range.from.format('DD.MM.')} – ${range.to.format('DD.MM.YYYY')}`
        : new Intl.DateTimeFormat(i18n.language, {month: 'long', year: 'numeric'}).format(anchor.toDate());

    if (accessLoading) {
        return <Box sx={{display: 'flex', justifyContent: 'center', p: 6}}><CircularProgress/></Box>;
    }

    if (!access?.available) {
        return (
            <Box sx={{p: {xs: 2, md: 3}, maxWidth: 900}}>
                <Typography variant="h5" gutterBottom>{t('calendar.title')}</Typography>
                <Alert severity={access?.hasLabelUser === false ? 'info' : 'warning'} sx={{mt: 2}}>
                    <AlertTitle>{t('calendar.unavailableTitle')}</AlertTitle>
                    {access?.message}
                    {access?.hasLabelUser === false && isAdmin && (
                        <Box sx={{mt: 1.5}}>
                            <Link component={RouterLink} to="/admin/users">{t('calendar.goToUsers')}</Link>
                        </Box>
                    )}
                    {access?.hasLabelUser === false && !isAdmin && (
                        <Box sx={{mt: 1.5}}>
                            <Typography variant="body2">{t('calendar.askAdmin')}</Typography>
                        </Box>
                    )}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{p: {xs: 1.5, md: 3}}}>
            {/* Kopfzeile: Person links, Steuerung rechts — auf schmalen Schirmen untereinander. */}
            <Stack direction={{xs: 'column', md: 'row'}} spacing={1.5}
                   sx={{alignItems: {md: 'center'}, justifyContent: 'space-between', mb: 1.5}}>
                <Box>
                    <Typography variant="h5">{t('calendar.title')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('calendar.subtitle', {
                            name: access.employee?.name ?? '—',
                            employeeId: access.employee?.employeeId ?? '—',
                            labelUser: access.labelUser ?? '—',
                        })}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1}
                       sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 1}}>
                    <ToggleButtonGroup size="small" exclusive value={mode}
                                       onChange={(_, value: RangeMode | null) => value && setMode(value)}>
                        <ToggleButton value="week">{t('calendar.modeWeek')}</ToggleButton>
                        <ToggleButton value="month">{t('calendar.modeMonth')}</ToggleButton>
                    </ToggleButtonGroup>

                    <Stack direction="row" spacing={0.5} sx={{alignItems: 'center'}}>
                        <Tooltip title={t('calendar.previous')}>
                            <IconButton size="small" onClick={() => setAnchor(anchor.subtract(1, step))}>
                                <FontAwesomeIcon icon={faAngleLeft}/>
                            </IconButton>
                        </Tooltip>
                        <Button size="small" variant="outlined"
                                onClick={() => setAnchor(dayjs().startOf('day'))}>
                            {t('calendar.today')}
                        </Button>
                        <Tooltip title={t('calendar.next')}>
                            <IconButton size="small" onClick={() => setAnchor(anchor.add(1, step))}>
                                <FontAwesomeIcon icon={faAngleRight}/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={t('calendar.reload')}>
                            <IconButton size="small" onClick={load} disabled={loading}>
                                <FontAwesomeIcon icon={faArrowsRotate}/>
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
            </Stack>

            <Stack direction="row" spacing={1}
                   sx={{alignItems: 'center', flexWrap: 'wrap', rowGap: 1, mb: 1.5}}>
                <Typography variant="subtitle1" sx={{fontWeight: 500}}>{rangeLabel}</Typography>
                <Chip size="small" label={t('calendar.entryCount', {count: items.length})}/>
                {!access.allowWrites && (
                    <Chip size="small" variant="outlined" label={t('calendar.readOnly')}/>
                )}
                {loading && <CircularProgress size={16}/>}
            </Stack>

            {error && <Alert severity="error" sx={{mb: 2}} onClose={() => setError(undefined)}>{error}</Alert>}

            {/*
              * Auf schmalen Schirmen die Tagesliste: ein Raster mit sieben Spalten ist auf einem
              * Telefon nicht lesbar, und ein waagerecht scrollender Kalender ist es erst recht nicht.
              */}
            {compact ? (
                <AgendaList from={range.from} to={range.to} items={items} now={now} onSelect={setSelected}/>
            ) : mode === 'week' ? (
                <WeekGrid anchor={anchor} items={items} now={now} onSelect={setSelected}/>
            ) : (
                <MonthGrid anchor={anchor} items={items} now={now} onSelect={setSelected}
                           onPickDay={(day) => {
                               setAnchor(day.startOf('day'));
                               setMode('week');
                           }}/>
            )}

            <EntryDialog item={selected} onClose={() => setSelected(null)}/>

            {/* Rohdaten der Antwort — für das Abnehmen der Anbindung gegen die Plantafel. */}
            {view && (
                <Box sx={{mt: 2}}>
                    <Button size="small" color="inherit" onClick={() => setShowRaw(!showRaw)}
                            startIcon={<FontAwesomeIcon icon={faCode}/>}>
                        {t('calendar.showRaw')}
                    </Button>
                    <Collapse in={showRaw}>
                        <Paper variant="outlined" sx={{mt: 1, p: 1.5, overflowX: 'auto'}}>
                            <Typography component="pre" variant="caption"
                                        sx={{m: 0, fontFamily: 'monospace', whiteSpace: 'pre'}}>
                                {JSON.stringify(view, null, 2)}
                            </Typography>
                        </Paper>
                    </Collapse>
                </Box>
            )}
        </Box>
    );
};

export default MyCalendar;
